import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  // "digit" targets are the individual input boxes
  // "hidden" is the hidden field that holds the full OTP for form submission
  static targets = ["digit", "hidden"];

  connect() {
    // Defer focus by one tick so the browser has fully committed the DOM before we try to focus.
    setTimeout(() => this.digitTargets[0]?.focus(), 0);
  }

  handleInput(event) {
    const input = event.target;

    // Strip any non-digit characters and keep only the first character.
    // This handles cases where the browser inserts more than one character (e.g. mobile autocomplete).
    input.value = input.value.replace(/\D/g, "").slice(0, 1);

    // Keep the hidden field in sync with the current digits so the form submits the correct value.
    this.syncHidden();

    if (input.value) {
      const next = this.getNext(input);
      if (next) {
        // A digit was entered — move focus to the next box.
        next.focus();
      }
    }
  }

  handleKeydown(event) {
    const input = event.target;

    if (event.key === "Backspace" && !input.value) {
      // If backspace is pressed on an already-empty box, move to the previous box and clear it.
      // preventDefault stops the browser from navigating back.
      event.preventDefault();
      const prev = this.getPrev(input);
      if (prev) {
        prev.focus();
        prev.value = "";
        this.syncHidden();
      }
    }

    // Arrow keys navigate between boxes without changing values.
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.getPrev(input)?.focus();
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.getNext(input)?.focus();
    }
  }

  handlePaste(event) {
    // Prevent the browser's default paste behaviour, which would insert text at the cursor position.
    event.preventDefault();

    // Extract only digits from the pasted text and limit to the number of boxes available.
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, this.digitTargets.length);

    // Fill each box with the corresponding pasted digit.
    digits.split("").forEach((d, i) => {
      if (this.digitTargets[i]) this.digitTargets[i].value = d;
    });

    this.syncHidden();

    // Move focus to the next empty box, or the last box if all are filled.
    const focusIndex = Math.min(digits.length, this.digitTargets.length - 1);
    this.digitTargets[focusIndex]?.focus();
  }

  syncHidden() {
    // Concatenate all digit box values into the hidden field so the form submits a single OTP string.
    this.hiddenTarget.value = this.digitTargets.map((i) => i.value).join("");
  }

  getNext(input) {
    // Returns the digit target immediately after the given input, or undefined if there is none.
    const idx = this.digitTargets.indexOf(input);
    return this.digitTargets[idx + 1];
  }

  getPrev(input) {
    // Returns the digit target immediately before the given input, or undefined if there is none.
    const idx = this.digitTargets.indexOf(input);
    return this.digitTargets[idx - 1];
  }
}
